import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { fmtPrice } from "@/components/market/PriceCell";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDeskStatus, useLiveMarkets } from "@/lib/useLiveMarkets";

const ASSETS = ["USDT", "BTC"];
const DEFAULT_MIN_INR = 1_000_000;
const OPEN = ["pending", "contacted", "processing"];
const STAGES = ["pending", "contacted", "processing", "confirmed"];
const STATUS = {
  pending: { label: "Pending", tone: "text-warning" },
  contacted: { label: "RM contacted", tone: "text-primary" },
  processing: { label: "Processing", tone: "text-primary" },
  confirmed: { label: "Settled", tone: "text-up" },
  expired: { label: "Expired", tone: "text-muted-foreground" },
  cancelled: { label: "Cancelled", tone: "text-down" },
  rejected: { label: "Rejected", tone: "text-down" },
};
const DESK_EMAIL = "otc@paisacx.io";

const inr = (v) => (Number.isFinite(Number(v)) ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—");
const qty = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString("en-US", { maximumFractionDigits: 8 }) : "—");

function CountdownRing({ progress, children }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-32 w-32">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="oklch(var(--surface-2))" strokeWidth="7" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke="url(#ringGrad)"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - progress)}
          style={{ transition: "stroke-dashoffset 0.25s linear" }}
        />
        <defs>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="oklch(var(--brand-emerald))" />
            <stop offset="100%" stopColor="oklch(var(--brand-lime))" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
}

function useReferenceRate(asset) {
  const { rows, usdtInr } = useLiveMarkets("INR");
  const v = asset === "USDT" ? Number(usdtInr) : Number(rows.find((r) => r.symbol === asset)?.price);
  return Number.isFinite(v) && v > 0 ? v : null;
}

export default function OtcPage() {
  const { user } = useAuth();
  const kycApproved = user?.kyc_status === "approved" || user?.role === "admin";
  const [side, setSide] = useState("buy");
  const [asset, setAsset] = useState("USDT");
  const [amountType, setAmountType] = useState("fiat");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [focusId, setFocusId] = useState(null);
  const [minTradeInr, setMinTradeInr] = useState(DEFAULT_MIN_INR);
  const ref = useReferenceRate(asset);
  const desks = useDeskStatus("INR");
  const paused = desks?.otc ? desks.otc[asset] === false : false;

  useEffect(() => {
    api
      .get("/api/otc/settings")
      .then(({ data }) => data?.min_trade_inr && setMinTradeInr(data.min_trade_inr))
      .catch(() => {});
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/api/otc/history");
      setHistory(data || []);
    } catch {
      /* keep the last list */
    }
  }, []);

  useEffect(() => {
    if (kycApproved) loadHistory();
  }, [kycApproved, loadHistory]);

  const active = useMemo(() => {
    if (focusId) return history.find((h) => h.quote_id === focusId) || null;
    return history.find((h) => OPEN.includes(h.status)) || null;
  }, [history, focusId]);

  useEffect(() => {
    if (!active || !OPEN.includes(active.status)) return undefined;
    const id = setInterval(loadHistory, 12000);
    return () => clearInterval(id);
  }, [active, loadHistory]);

  const notional = amountType === "fiat" ? Number(amount || 0) : Number(amount || 0) * (ref || 0);
  const sizeError =
    amount && notional > 0 && notional < minTradeInr ? `Ticket size must be at least ₹${minTradeInr.toLocaleString("en-IN")}.` : null;

  async function submit() {
    if (!amount || Number(amount) <= 0) return setErr("Enter an amount");
    if (sizeError) return setErr(sizeError);
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(
        "/api/otc/quote",
        { side, asset, amount_type: amountType, amount: Number(amount) },
        { timeout: 30000 },
      );
      setFocusId(data?.request?.quote_id || null);
      setAmount("");
      loadHistory();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(quoteId) {
    try {
      await api.post(`/api/otc/quote/${quoteId}/cancel`);
    } catch {
      /* status refresh below shows the outcome */
    }
    setFocusId(null);
    loadHistory();
  }

  const stage = active ? STAGES.indexOf(active.status) : -1;
  const progress = active ? (active.status === "confirmed" ? 1 : (stage + 1) / STAGES.length) : 0;

  return (
    <AppShell bare>
      <div className="page-fade mx-auto max-w-7xl px-4 py-10">
        <h1 className="text-3xl font-semibold">OTC desk</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Block trades from ₹{minTradeInr.toLocaleString("en-IN")}. Your relationship manager confirms the final rate before
          anything executes.
        </p>

        <div className="mt-8 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-2xl border border-border bg-surface/50 p-6" data-testid="otc-rfq-form">
            <div className="flex gap-1 rounded-lg border border-border bg-surface-2 p-1 sm:w-fit">
              {["buy", "sell"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSide(s)}
                  data-testid={`rfq-side-${s}`}
                  className={`flex-1 rounded-md px-4 py-1.5 text-sm font-medium capitalize transition sm:flex-none ${
                    side === s ? (s === "buy" ? "bg-up/20 text-up" : "bg-down/20 text-down") : "text-muted-foreground"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground" htmlFor="otc-coin">
                  Pair
                </label>
                <select
                  id="otc-coin"
                  value={asset}
                  onChange={(e) => setAsset(e.target.value)}
                  data-testid="rfq-asset"
                  className="mt-1.5 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary"
                >
                  {ASSETS.map((a) => (
                    <option key={a} value={a}>
                      {a} / INR{desks?.otc && desks.otc[a] === false ? " — paused" : ""}
                    </option>
                  ))}
                </select>
                {paused && (
                  <p className="mt-1.5 text-xs text-down" data-testid="otc-paused">
                    The desk is not taking {asset} requests right now. Try again later or pick another pair.
                  </p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-muted-foreground" htmlFor="otc-amount">
                    Amount ({amountType === "fiat" ? "INR" : asset})
                  </label>
                  <button
                    type="button"
                    onClick={() => setAmountType((t) => (t === "fiat" ? "asset" : "fiat"))}
                    data-testid="rfq-toggle-amount-type"
                    className="text-[11px] text-primary hover:underline"
                  >
                    Enter in {amountType === "fiat" ? asset : "INR"}
                  </button>
                </div>
                <input
                  id="otc-amount"
                  value={amount}
                  inputMode="decimal"
                  data-testid="rfq-amount"
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                  placeholder={amountType === "fiat" ? "10,00,000" : "0.5"}
                  className="num mt-1.5 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col items-center gap-6 rounded-xl border border-border bg-background/40 p-6 sm:flex-row">
              <CountdownRing progress={progress}>
                <span className="num text-2xl font-semibold">{active ? `${Math.max(stage, 0) + 1}/${STAGES.length}` : "—"}</span>
                <span className="text-[10px] text-muted-foreground">{active ? "stage" : "no request"}</span>
              </CountdownRing>
              <div className="flex-1 text-center sm:text-left" data-testid={active ? "otc-request-pending" : undefined}>
                {active ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {active.side === "buy" ? "Buying" : "Selling"} {active.asset} ·{" "}
                      <span className={STATUS[active.status]?.tone}>{STATUS[active.status]?.label || active.status}</span>
                    </p>
                    <p className="num text-3xl font-semibold">
                      {active.quoted_price_inr != null ? inr(active.quoted_price_inr) : "Awaiting RM quote"}
                    </p>
                    <p className="num mt-1 text-sm text-muted-foreground">
                      {active.asset_qty != null ? `${qty(active.asset_qty)} ${active.asset}` : "—"} ≈{" "}
                      {active.fiat_total_inr != null ? inr(active.fiat_total_inr) : "—"}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {active.status === "confirmed"
                        ? "Trade settled — balances are updated in your wallet."
                        : "Your RM will call or email your registered contacts with the firm rate."}
                    </p>
                  </>
                ) : ref ? (
                  <>
                    <p className="text-xs text-muted-foreground">Reference rate</p>
                    <p className="num text-3xl font-semibold">₹{fmtPrice(ref)}</p>
                    <p className="num mt-1 text-sm text-muted-foreground">
                      {amount
                        ? amountType === "fiat"
                          ? `₹${Number(amount).toLocaleString("en-IN")} ≈ ${qty(Number(amount) / ref)} ${asset}`
                          : `${amount} ${asset} ≈ ₹${(Number(amount) * ref).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
                        : `Live ${asset}/INR · indicative only`}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Submit a request and the desk prices it manually. Nothing executes until you confirm with your RM.
                    </p>
                    {sizeError && <p className="mt-1 text-xs text-down">{sizeError}</p>}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Request a quote to reach the desk. Nothing executes until you confirm the rate with your RM.
                  </p>
                )}
                <div className="mt-4 flex flex-wrap justify-center gap-2 sm:justify-start">
                  {active && OPEN.includes(active.status) ? (
                    <button
                      type="button"
                      onClick={() => cancel(active.quote_id)}
                      data-testid="otc-request-cancel"
                      className="rounded-lg border border-border bg-surface-2 px-4 py-2 text-sm transition hover:border-primary"
                    >
                      Cancel request
                    </button>
                  ) : active ? (
                    <button
                      type="button"
                      onClick={() => setFocusId("__none__")}
                      className="rounded-lg border border-border bg-surface-2 px-4 py-2 text-sm transition hover:border-primary"
                    >
                      New request
                    </button>
                  ) : null}
                  {!active && (
                    <button
                      type="button"
                      onClick={submit}
                      disabled={busy || !kycApproved || paused}
                      data-testid="rfq-get-quote"
                      className="brand-gradient rounded-lg px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40"
                    >
                      {busy ? "Sending…" : "Request a quote"}
                    </button>
                  )}
                  {active?.status === "confirmed" && (
                    <Link to="/wallets" className="brand-gradient rounded-lg px-4 py-2 text-sm font-semibold text-primary-foreground">
                      View wallet
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {err && <p className="mt-3 rounded-lg border border-down/40 bg-down/10 px-3 py-2 text-xs text-down">{err}</p>}
            {!kycApproved && (
              <p className="mt-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning" data-testid="otc-kyc-required">
                OTC trading is only open to KYC-verified accounts.{" "}
                <Link to="/profile/kyc" className="underline">
                  Complete verification
                </Link>{" "}
                to request a quote.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-surface/50 p-6">
            <p className="text-xs text-muted-foreground">Your relationship manager</p>
            <div className="mt-4 flex items-center gap-4">
              <span className="brand-gradient flex h-14 w-14 items-center justify-center rounded-full text-lg font-semibold text-primary-foreground">
                OD
              </span>
              <div>
                <p className="font-medium">PaisaCX OTC desk</p>
                <p className="text-xs text-muted-foreground">Dedicated coverage for treasuries, family offices and HNIs</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Tickets from ₹10 L to ₹5 Cr+ settle off-book in one price, with no slippage on the public order book.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <Link
                to="/contact"
                className="rounded-lg border border-border bg-surface-2 py-2 text-center text-sm transition hover:border-primary"
              >
                Raise a ticket
              </Link>
              <a href={`mailto:${DESK_EMAIL}`} className="rounded-lg border border-border bg-surface-2 py-2 text-center text-sm transition hover:border-primary">
                Email the desk
              </a>
            </div>
          </div>
        </div>

        <h2 className="mt-12 text-xl font-semibold">OTC trade history</h2>
        <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-surface/40">
          <div className="min-w-[700px]">
            <div className="table-head grid grid-cols-[1fr_1fr_1fr_1fr_100px_90px] gap-3 px-4 py-2.5">
              <span>Ticket</span>
              <span>Pair</span>
              <span className="text-right">Size</span>
              <span className="text-right">Rate</span>
              <span>Status</span>
              <span className="text-right">Date</span>
            </div>
            {history.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="otc-history-empty">
                No OTC tickets yet.
              </p>
            ) : (
              history.map((h) => (
                <div key={h.quote_id} data-testid={`otc-history-row-${h.quote_id}`} className="terminal-row grid-cols-[1fr_1fr_1fr_1fr_100px_90px] gap-3 px-4 py-3 text-sm">
                  <span className="num truncate text-muted-foreground">{h.quote_id}</span>
                  <span>
                    <span className={h.side === "buy" ? "text-up" : "text-down"}>{h.side === "buy" ? "Buy" : "Sell"}</span> {h.asset}/INR
                  </span>
                  <span className="num text-right">{h.asset_qty != null ? qty(h.asset_qty) : "—"}</span>
                  <span className="num text-right">{h.quoted_price_inr != null ? inr(h.quoted_price_inr) : "—"}</span>
                  <span className={`text-xs ${STATUS[h.status]?.tone || "text-muted-foreground"}`}>{STATUS[h.status]?.label || h.status}</span>
                  <span className="num text-right text-muted-foreground">
                    {h.created_at ? new Date(h.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
